import { useState, useEffect } from "react";
import PlatformFooter from "@/components/PlatformFooter";
import PlatformLogo, { PLATFORM_SUBTITLE } from "@/components/PlatformLogo";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PasswordInput } from "@/components/PasswordInput";
import LanguageSelector from "@/components/LanguageSelector";

export default function Login() {
  const { login, error, user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav("/"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0F172A]">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1758801305205-edbf3de97b1b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative"><PlatformLogo size={44} dark testId="login-platform-logo-desktop" /></div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight">{t("loginhero.title_1")}<br />{t("loginhero.title_2")}</h1>
          <p className="text-slate-300 mt-4 max-w-md">{t("loginhero.sub")}</p>
        </div>
        <div className="relative"><PlatformFooter /></div>
      </div>

      <div className="flex items-center justify-center p-6 bg-[#F8FAFC] relative">
        <div className="absolute top-4 right-4"><LanguageSelector variant="light" /></div>
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden mb-8"><PlatformLogo size={44} dark={false} testId="login-platform-logo" /></div>
          <h2 className="font-heading text-2xl font-bold text-[#0F172A]">{t("auth.login_title")}</h2>
          <p className="text-sm text-slate-500 mb-6">{PLATFORM_SUBTITLE}</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="mt-1">
                <PasswordInput id="password" data-testid="login-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Link to="/forgot-password" data-testid="forgot-link" className="block text-right text-xs text-sky-600 hover:text-sky-700 mt-1.5">{t("auth.forgot_link")}</Link>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600" role="alert">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B]">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {t("auth.sign_in")}
            </Button>
            <p className="text-sm text-slate-500 text-center pt-2">
              {t("auth.no_account")} <Link to="/register" data-testid="login-register-link" className="text-sky-600 hover:text-sky-700 font-medium">{t("auth.register_cta")}</Link>
              <span className="mx-2 text-slate-300">·</span>
              <Link to="/welcome" data-testid="login-home-link" className="text-slate-500 hover:text-[#0F172A]">Home</Link>
            </p>
          </div>
          <div className="lg:hidden mt-6"><PlatformFooter /></div>
        </form>
      </div>
    </div>
  );
}
